/**
 * MemberChatPage — dedicated full-page chat with an AI Member.
 *
 * Route: /team/m/:memberId/chat
 *
 * This is the canonical destination for any "Chat" affordance — the Chat
 * button on the member detail page, future buttons on member cards / roster
 * rows, etc. The page hosts the same <ChatTab> panel used to be embedded
 * under the Member detail's tabs, but with its own chrome (back link, live
 * pill, overflow) and full-viewport height.
 */

import { Link, useNavigate, useParams } from 'react-router-dom';
import { Avatar } from '@/components/Avatar/Avatar';
import { Button } from '@/components/Button/Button';
import { Alert } from '@/components/Alert/Alert';
import { ApiError } from '@/lib/api';
import { t } from '@/lib/i18n';
import { formatPersonName } from '@/lib/format';
import { useMember } from '../hooks';
import { ChatTab } from './ChatTab';
import styles from './MemberChatPage.module.css';

export function MemberChatPage() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useMember(memberId);

  if (isLoading) {
    return (
      <div className={styles.state} role="status" aria-live="polite">
        {t('Loading…')}
      </div>
    );
  }
  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className={styles.state} role="alert">
        <p>{t("That Member isn't available.")}</p>
        <Link to="/team">
          <Button variant="secondary">{t('Back to team')}</Button>
        </Link>
      </div>
    );
  }
  if (error || !data) {
    return <Alert variant="error">{t("Couldn't load this Member.")}</Alert>;
  }

  const { member } = data;
  const name = formatPersonName(member);

  // Humans don't have chat per ui-design.md §5.6 — redirect to their profile.
  if (member.kind !== 'ai') {
    void navigate(`/team/m/${encodeURIComponent(member.id)}`, { replace: true });
    return null;
  }

  const profileHref = `/team/m/${encodeURIComponent(member.id)}`;
  const activity = member.positions[0]?.name ?? t('Available');

  return (
    <div className={styles.page}>
      {/* ─── Top action bar ───────────────────────────────────────── */}
      <div className={styles.topBar}>
        <Link to={profileHref} className={styles.back}>
          <Button variant="ghost" size="sm">
            {t('← {name}’s profile', { name })}
          </Button>
        </Link>
        <div className={styles.topActions}>
          <span className={styles.live}>
            <span className={styles.liveDot} aria-hidden="true" />
            {t('Live')}
          </span>
          <button type="button" className={styles.overflow} aria-label={t('More actions')} disabled>
            <span aria-hidden="true">⋯</span>
          </button>
        </div>
      </div>

      {/* ─── Hero strip: avatar + name + activity ─────────────────── */}
      <div className={styles.hero}>
        <Avatar name={name} size="md" kind={member.kind} />
        <div className={styles.heroBody}>
          <div className={styles.heroName}>{name}</div>
          <div className={styles.heroActivity}>{activity}</div>
        </div>
      </div>

      {/* ─── Chat panel ───────────────────────────────────────────── */}
      <ChatTab member={member} />
    </div>
  );
}
